/**
 * CertificatesCategoryList — iter-217c-v17
 *
 * Per user request (Feb 2026):
 *   When the admin picks a recipient category (merchants / customers /
 *   company / employees) from CertificatesHub, they land here. This page
 *   shows every certificate in that bucket grouped by status:
 *
 *     • الكل           — All
 *     • بانتظار الاعتماد — Pending approval
 *     • معتمدة          — Approved
 *
 *   Plus a prominent "إدراج شهادة جديدة" button that opens the create
 *   page. Approved certificates can be downloaded as PDF unlimited times.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Award, Plus, Loader2, Store, Users, Building2, Briefcase, ChevronLeft,
  ChevronRight, Clock, CheckCircle2, XCircle, Download, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import { Button } from '../../components/ui/button';
import DashboardLayout from '../../components/DashboardLayout';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  merchant: { ar: 'منصة التجار',  en: 'Merchants',  icon: Store,     color: '#8B5CF6' },
  customer: { ar: 'منصة العملاء', en: 'Customers',  icon: Users,     color: '#1E9BD9' },
  company:  { ar: 'للشركة',        en: 'Company',    icon: Building2, color: '#F59E0B' },
  employee: { ar: 'للموظفين',     en: 'Employees',  icon: Briefcase, color: '#10b981' },
};

const STATUS_TABS = [
  { id: 'all',              ar: 'الكل',              en: 'All',      color: '#94a3b8' },
  { id: 'pending_approval', ar: 'بانتظار الاعتماد', en: 'Pending',  color: '#f59e0b' },
  { id: 'approved',         ar: 'معتمدة',           en: 'Approved', color: '#10b981' },
];

export default function CertificatesCategoryList() {
  const { category } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { getAuthHeaders: authHeaders } = useAuth();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);
  const Arrow = isAr ? ChevronLeft : ChevronRight;

  const meta = CATEGORY_META[category] || CATEGORY_META.customer;
  const CatIcon = meta.icon;

  const [rows,   setRows]   = useState([]);
  const [status, setStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState({});

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = { category };
      if (status !== 'all') params.status = status;
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/certificates/list`, {
        headers: authHeaders(),
        params,
      });
      setRows(Array.isArray(data?.data) ? data.data : []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('فشل تحميل القائمة', 'Failed to load'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [category, status, authHeaders]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const counts = useMemo(() => ({
    pending_approval: rows.filter((r) => r.status === 'pending_approval').length,
    approved:         rows.filter((r) => r.status === 'approved').length,
  }), [rows]);

  const onDelete = async (cert) => {
    if (!window.confirm(t(
      `حذف الشهادة ${cert.id}؟`,
      `Delete certificate ${cert.id}?`,
    ))) return;
    setDeleting((d) => ({ ...d, [cert.id]: true }));
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/certificates/${cert.id}`, {
        headers: authHeaders(),
      });
      toast.success(t('تم الحذف', 'Deleted'));
      setRows((prev) => prev.filter((r) => r.id !== cert.id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('فشل الحذف', 'Delete failed'));
    } finally {
      setDeleting((d) => ({ ...d, [cert.id]: false }));
    }
  };

  return (
    <DashboardLayout
      title={`الشهادات — ${meta.ar}`}
      titleEn={`Certificates — ${meta.en}`}
      subtitle="إدارة الشهادات لهذه الفئة — عرض المعتمدة والمعلّقة"
      subtitleEn="Manage certificates for this category — view approved and pending items"
      screenCode="FZLC-CERT-LIST"
      backPath="/manage/cloudgm-fz-llc/certificates"
      onBack={() => navigate('/manage/cloudgm-fz-llc/certificates')}
      breadcrumb={[
        { labelAr: 'لوحة التحكم', labelEn: 'Dashboard', path: '/dashboard' },
        { labelAr: 'إدارة شركة CLOUDGM FZ LLC', labelEn: 'CLOUDGM FZ LLC Management', path: '/manage/cloudgm-fz-llc' },
        { labelAr: 'الشهادات', labelEn: 'Certificates', path: '/manage/cloudgm-fz-llc/certificates' },
        { labelAr: meta.ar, labelEn: meta.en },
      ]}
    >
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid={`cert-list-${category}`}>
        {/* Header row: category badge + New button */}
        <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
          <div className="flex items-center gap-3">
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center border"
              style={{ backgroundColor: `${meta.color}15`, borderColor: `${meta.color}40` }}
            >
              <CatIcon className="w-5 h-5" style={{ color: meta.color }} />
            </div>
            <div>
              <div className="text-white font-bold text-base">{isAr ? meta.ar : meta.en}</div>
              <div className="text-[11px] text-white/50">{t(`${rows.length} شهادة`, `${rows.length} certificates`)}</div>
            </div>
          </div>

          <Button
            onClick={() => navigate(`/manage/cloudgm-fz-llc/certificates/${category}/create`)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold h-11 px-5"
            data-testid="new-certificate-btn"
          >
            <Plus className="w-4 h-4 me-1.5" />
            {t('إدراج شهادة جديدة', 'Insert new certificate')}
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-5 overflow-x-auto no-scrollbar" data-testid="cert-status-tabs">
          {STATUS_TABS.map((tab) => {
            const active = status === tab.id;
            const count = tab.id === 'all' ? rows.length : counts[tab.id] ?? 0;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setStatus(tab.id)}
                className={`inline-flex items-center gap-2 px-4 h-10 rounded-xl text-sm font-semibold whitespace-nowrap transition border ${
                  active
                    ? 'bg-white/10 text-white border-white/25'
                    : 'bg-white/[0.02] text-white/60 border-white/10 hover:border-white/25 hover:text-white/90'
                }`}
                data-testid={`cert-tab-${tab.id}`}
              >
                <span className="w-2 h-2 rounded-full" style={{ background: tab.color }} />
                <span>{isAr ? tab.ar : tab.en}</span>
                <span className="text-[11px] text-white/50">({count})</span>
              </button>
            );
          })}
        </div>

        {/* List */}
        {loading ? (
          <div className="p-16 flex justify-center">
            <Loader2 className="h-7 w-7 animate-spin text-white/40" />
          </div>
        ) : rows.length === 0 ? (
          <div className="bg-white/[0.02] backdrop-blur-xl border border-dashed border-white/15 rounded-2xl p-14 text-center">
            <Award className="w-10 h-10 text-white/25 mx-auto mb-3" />
            <div className="text-white/60 text-sm mb-1">
              {t('لا توجد شهادات في هذا التصنيف بعد', 'No certificates in this bucket yet')}
            </div>
            <div className="text-[11px] text-white/40">
              {t('اضغط «إدراج شهادة جديدة» لإصدار أول شهادة', 'Click "Insert new certificate" to issue the first one')}
            </div>
          </div>
        ) : (
          <ul className="space-y-2" data-testid="cert-list-rows">
            {rows.map((cert) => (
              <CertRow
                key={cert.id}
                cert={cert}
                isAr={isAr}
                onOpen={() => navigate(`/manage/cloudgm-fz-llc/certificates/${category}/view/${cert.id}`)}
                onDelete={() => onDelete(cert)}
                deleting={!!deleting[cert.id]}
                t={t}
                Arrow={Arrow}
              />
            ))}
          </ul>
        )}
      </section>
    </DashboardLayout>
  );
}

function StatusPill({ status, isAr }) {
  const styleMap = {
    pending_approval: { bg: '#f59e0b20', color: '#fbbf24', border: '#f59e0b60', Icon: Clock,        ar: 'بانتظار الاعتماد', en: 'Pending' },
    approved:         { bg: '#10b98120', color: '#34d399', border: '#10b98160', Icon: CheckCircle2, ar: 'معتمدة',            en: 'Approved' },
    rejected:         { bg: '#ef444420', color: '#f87171', border: '#ef444460', Icon: XCircle,      ar: 'مرفوضة',            en: 'Rejected' },
  };
  const s = styleMap[status] || styleMap.pending_approval;
  const IconC = s.Icon;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border"
      style={{ backgroundColor: s.bg, color: s.color, borderColor: s.border }}
    >
      <IconC className="w-3 h-3" />
      {isAr ? s.ar : s.en}
    </span>
  );
}

function CertRow({ cert, isAr, onOpen, onDelete, deleting, t, Arrow }) {
  return (
    <motion.li
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className="group bg-white/[0.02] hover:bg-white/[0.04] border border-white/10 hover:border-white/25 rounded-xl p-4 transition"
      data-testid={`cert-row-${cert.id}`}
    >
      <div className="flex items-center gap-3 flex-wrap">
        {/* Left: name + subject */}
        <button
          type="button"
          onClick={onOpen}
          className="flex-1 min-w-0 text-start"
        >
          <div className="text-white font-semibold text-sm truncate">
            {cert.recipient_name || '—'}
          </div>
          <div className="text-[12px] text-white/55 truncate mt-0.5">
            {(isAr ? cert.subject_ar : cert.subject_en) || cert.subject_ar || '—'}
          </div>
        </button>

        {/* Middle: status + date */}
        <div className="flex items-center gap-3 flex-wrap">
          <StatusPill status={cert.status} isAr={isAr} />
          <span className="text-[11px] text-white/45 font-mono" dir="ltr">
            {cert.cert_no}
          </span>
          <span className="text-[11px] text-white/45" dir="ltr">
            {(cert.created_at || '').slice(0, 10)}
          </span>
        </div>

        {/* Right: actions */}
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={onOpen}
            title={t('عرض / تحميل', 'View / Download')}
            className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10"
            data-testid={`cert-view-${cert.id}`}
          >
            {cert.status === 'approved'
              ? <Download className="w-4 h-4" />
              : <Arrow className="w-4 h-4" />}
          </button>
          <button
            type="button"
            onClick={onDelete}
            disabled={deleting}
            title={t('حذف', 'Delete')}
            className="p-2 rounded-lg text-white/45 hover:text-rose-400 hover:bg-rose-500/10 disabled:opacity-40"
            data-testid={`cert-delete-${cert.id}`}
          >
            {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </motion.li>
  );
}
